/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE POST-APPROVAL PAUSE — ONE COPY DECK, ONE STATUS SET
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Owner, 2026-09-13, three messages that are one ruling:
 *   1. *"We need the pause and check. Make sure the wording is correct for each
 *      stage. 'This order has been approved' works for approvals but should be
 *      'This order has already been ordered' for orders which are already
 *      ordered. Please include this feature as it stops edits after the fact
 *      as a supervisor may change the quantity after the buyer has placed the
 *      order and not realise it's been placed."*
 *   2. *"There should definitely be a warning and check to ask if we do want to
 *      remove the item as it's already been ordered and marked as delivered."*
 *      — and, for a line already TICKED received, *"strong warning with text
 *      input option like 1."*
 *   3. *"can you use the post approval pop up warning? seems like we should be
 *      using common popup style for editing orders etc?"* — ONE dialog,
 *      `components/PostApprovalEditDialog.js`, used everywhere.
 *   And on the reason box: *"The person does not need to enter text but the
 *   text box should be visible."*
 *
 * 🔑 **EVERY STRING THE DIALOG SHOWS COMES FROM HERE.** The inline editor, the
 * item-removal path in `OrderForm` and the full order page all render the one
 * component, and that component reads this module — so a stage's wording has
 * exactly one place to be wrong. `postApprovalEdit.test.js` pins every stage ×
 * action as LITERALS, never derived from this file.
 *
 * 🔑 **`POST_APPROVAL_STATUSES` IS THE SERVER'S SET, MIRRORED, NEVER A GATE.**
 * `server/helpers/editPermissions.js` refuses non-managers at exactly these
 * statuses and `PUT /api/orders/:id` writes `post_approval_edit` for exactly
 * these; this copy only decides whether to ASK. Nobody reaches the editor at
 * these statuses without authority the server already granted — the pause is
 * aimed at the managers and admins who can.
 */
import { OVERRIDE_REASON_MAX } from './overrideReason';

export const POST_APPROVAL_STATUSES = Object.freeze([
  'approved',
  'ordered',
  'partially_delivered',
  'delivered',
  'cancelled',
]);

/** True when an edit to an order at `status` must pause and ask first. */
export function isPostApprovalStatus(status) {
  return POST_APPROVAL_STATUSES.includes(status);
}

/**
 * The lowercase phrase for each stage, for sentences that embed the status
 * (`OrderPage.js`'s blue "Order is <phrase> — changes won't affect the status"
 * note). Moved here from `OrderPage.js`'s private `POST_APPROVAL_LABELS`.
 */
export const POST_APPROVAL_STATUS_PHRASE = Object.freeze({
  approved: 'approved',
  ordered: 'ordered',
  partially_delivered: 'partially delivered',
  delivered: 'delivered',
  cancelled: 'cancelled',
});

/**
 * THE stage sentence — the owner's distinction, verbatim in shape: "has been"
 * for a DECISION (approved, cancelled), "has already been" for an ACT on the
 * goods (ordered, delivered), because the second is what someone editing
 * "after the fact" did not realise had happened.
 */
export const POST_APPROVAL_STAGE_SENTENCE = Object.freeze({
  approved: 'This order has been approved.',
  ordered: 'This order has already been ordered.',
  partially_delivered: 'This order has already been partially delivered.',
  delivered: 'This order has already been delivered.',
  cancelled: 'This order has been cancelled.',
});

/** The reason box label. Always visible; never gates Confirm. */
export const POST_APPROVAL_REASON_LABEL = 'Reason (optional)';

/**
 * The two things the pause guards.
 *
 * 🔻 `REMOVE_RECEIVED_ITEM` ("Remove received item" anyway) is DELETED —
 * owner ruling 2026-09-28: *"the fisrt pop up says untick delived before
 * deleteing. then if they do that and then click delete on the action menu it
 * gives a warning that the proce will be removed from the budget and are they
 * sure and requires a note."* A received line is no longer removable from the
 * client at all — it gets `RECEIVED_ITEM_REMOVAL_NOTICE` (OK only) — and the
 * server refuses it too (`received_item_not_removable`, `PUT /api/orders/:id`).
 */
export const POST_APPROVAL_ACTIONS = Object.freeze({
  SAVE: 'save',
  REMOVE_ITEM: 'remove-item',
});

/**
 * THE REMOVAL NOTE IS REQUIRED (owner, 2026-09-28: *"requires a note"*) — the
 * one place this dialog family's reason box gates Confirm. The Save pause's box
 * stays optional (`POST_APPROVAL_REASON_LABEL`); the 2026-09-13 ruling *"The
 * person does not need to enter text"* still governs the SAVE, and only the
 * removal was re-ruled. The server refuses a removal without it
 * (`removal_reason_required`), reading the per-line `removal_reasons` field.
 */
export const REMOVAL_REASON_LABEL = 'Note (required)';

/** The blocking notice a delivered line's Remove opens instead of a delete. */
export const RECEIVED_ITEM_REMOVAL_NOTICE = Object.freeze({
  title: 'Untick delivered first',
  message: "This item is marked as delivered. Untick 'Mark as delivered' before removing it.",
  okLabel: 'OK',
});

/**
 * Removing the ONLY line in the form (owner rulings 2026-10-01, reversing the
 * 2026-09-28 "disabled on the last line"). "Only" counts the lines in the FORM
 * (saved + unsaved), never the stored order. An order needs ≥ 1 line, so the
 * remove becomes a cancel: this dialog, whose "Cancel order" hands over to the
 * page's EXISTING cancel flow (`OrderForm`'s `onRequestCancelOrder`) — never a
 * second cancellation path. "Keep item" takes initial focus.
 */
export const ONLY_ITEM_REMOVAL = Object.freeze({
  title: 'Remove the only item?',
  message: 'An order needs at least one item, so removing this one cancels the whole order.',
  keepLabel: 'Keep item',
  cancelOrderLabel: 'Cancel order',
});

/** The only line's Remove, offered to a caller who may cancel here (a tooltip). */
export const ONLY_ITEM_REMOVE_TOOLTIP = 'This is the only item — removing it cancels the order.';

/** Why the only line's Remove is DISABLED: this caller may not cancel the order here. */
export const ONLY_ITEM_REMOVE_DENIED_TOOLTIP =
  "This is the only item — removing it would cancel the order, and you can't cancel this order here.";

/**
 * How a line item is named inside a sentence: its description in quotes, or
 * "item N" when it has none. Shared by the dialog and by the audit note the
 * removal leaves (`removalReasonNote`), so the two name one line one way.
 */
export function postApprovalItemLabel(item, index) {
  const description = item && typeof item.description === 'string' ? item.description.trim() : '';
  if (description) return `"${description}"`;
  return `item ${Number.isInteger(index) ? index + 1 : ''}`.trim();
}

/**
 * The finished copy for one dialog. Unknown status → a neutral stage sentence
 * rather than a blank one (the caller should not open the pause at all then,
 * but a dialog missing its first sentence would read as a bug).
 *
 * @returns {{ title, stageSentence, actionSentence, confirmLabel, cancelLabel, variant, reasonRequired, reasonLabel }}
 *
 * 🔑 **NO BUTTON READS `Cancel`.** The house rule for this dialog family
 * (`client/src/pages/CLAUDE.md`, and `constants/saveThenActPrompt.js`): every
 * button says what it does. This pause can sit directly after `Save and cancel
 * order`, where a `Cancel` button would mean the opposite of its neighbour.
 */
export function postApprovalDialogCopy({ status, action, itemLabel, amountText, adjustmentsText, countsInBudget }) {
  const stageSentence = POST_APPROVAL_STAGE_SENTENCE[status] || 'This order is past approval.';
  const label = itemLabel || 'this item';
  if (action === POST_APPROVAL_ACTIONS.REMOVE_ITEM) {
    // 💷 THE MONEY IS NAMED (owner, 2026-09-28: *"a warning that the proce will
    // be removed from the budget"*). "and the budget" is said ONLY when true:
    // budget spend sums `order_items.line_total` for lines carrying a cost code
    // or pot on a non-cancelled order (`server/helpers/budgetSpend.js`), so the
    // caller passes `countsInBudget` from those facts — an uncoded line leaves
    // no budget figure, and saying it did would be a false warning.
    // `adjustmentsText`: the line's price-adjustment children, which the
    // server's ON DELETE CASCADE removes with it (fix round 1, 2026-09-28).
    const including = adjustmentsText ? ` (including ${adjustmentsText} of price adjustments)` : '';
    const price = amountText ? `its price, ${amountText}${including},` : 'its price';
    return {
      title: 'Remove this item?',
      stageSentence,
      actionSentence: `Removing ${label} takes ${price} off the order total${countsInBudget ? ' and the budget' : ''}. Are you sure?`,
      confirmLabel: 'Remove item',
      cancelLabel: 'Keep item',
      // Red — the owner asked for "a warning", and the removal deletes money
      // from the order. Was 'default' while the note was optional.
      variant: 'danger',
      reasonRequired: true,
      reasonLabel: REMOVAL_REASON_LABEL,
    };
  }
  return {
    title: 'Save changes to this order?',
    stageSentence,
    actionSentence: 'Save these changes anyway?',
    confirmLabel: 'Save changes',
    cancelLabel: 'Keep editing',
    // POL2 A1 (owner ruling 2026-09-25): a Save is the green BTN.confirm —
    // ConfirmDialog's opt-in 'save' variant. The removal above is not a save
    // and keeps its own variant.
    variant: 'save',
    reasonRequired: false,
    reasonLabel: POST_APPROVAL_REASON_LABEL,
  };
}

/**
 * The audit note a CONFIRMED removal carries into the Save's `edit_reason`:
 * `Removed "<item>": <reason>`. Blank reason → `null` (the removal itself is
 * already in the field-level diff; an empty note would add nothing).
 */
export function removalReasonNote(label, reason) {
  const text = typeof reason === 'string' ? reason.trim() : '';
  if (!text) return null;
  return `Removed ${label}: ${text}`;
}

/**
 * The cap on `edit_reason` — a CLIENT MIRROR of the server's `EDIT_REASON_MAX`
 * (`server/helpers/orderEditDiff.js`, itself `OVERRIDE_REASON_MAX`). A
 * convenience, never the gate: the server refuses an over-long reason with a
 * 400 and writes nothing. It exists so the client can never SEND one — the
 * dialog's textarea is capped with it, and `buildEditReason` caps the combined
 * string. 🔀 A named sibling of `constants/overrideReason.js#OVERRIDE_REASON_MAX`
 * since 2026-09-14 (the server's own shape) — never a bare 2000 anywhere else.
 */
export const EDIT_REASON_MAX = OVERRIDE_REASON_MAX;

/**
 * Joins parts into one string. Blank parts drop out; nothing left → `''`.
 */
export function combineEditReasons(parts) {
  return (parts || [])
    .map((p) => (typeof p === 'string' ? p.trim() : ''))
    .filter(Boolean)
    .join('; ');
}

const ELLIPSIS = '…';

/**
 * THE `edit_reason` a Save sends: the reason typed into the Save pause, then
 * the notes of any confirmed removals — never longer than `EDIT_REASON_MAX`.
 *
 * 🔑 **THE TYPED SAVE REASON IS KEPT WHOLE; THE REMOVAL NOTES ARE WHAT GET
 * TRUNCATED** (with an ellipsis). The Save reason is what the user wrote about
 * THIS save and is already capped by the textarea; removal notes are prefixed
 * copies of reasons typed earlier and can stack past the cap. Without this, an
 * over-long combination 400s every Save — and a confirmed removal's note
 * survives a failed PUT — so the edit could not be saved until discarded
 * (review M3, 2026-09-13). Blank → `''`, and the caller sends no key.
 */
export function buildEditReason(saveReason, removalNotes) {
  const save = (typeof saveReason === 'string' ? saveReason.trim() : '').slice(0, EDIT_REASON_MAX);
  const notes = combineEditReasons(removalNotes);
  if (!notes) return save;
  const truncateNotes = (room) => (notes.length <= room ? notes : `${notes.slice(0, Math.max(0, room - 1))}${ELLIPSIS}`);
  if (!save) return truncateNotes(EDIT_REASON_MAX);
  const joined = `${save}; ${notes}`;
  if (joined.length <= EDIT_REASON_MAX) return joined;
  const room = EDIT_REASON_MAX - save.length - 2; // the "; " joiner
  if (room < 2) return save;
  return `${save}; ${truncateNotes(room)}`;
}
